import * as React from 'react';
import autobind from 'autobind-decorator';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { resolve } from 'inversify-react';

import { DataLocalService } from 'src/core/services/data-local.service';
import { PostcodeService } from 'src/core/services/postcode.service';
import { ContextService } from 'src/core/services/context.service';
import { AutocompleteListDataService } from 'src/core/ui/autocomplete/autocomplete.data';
import { RegionListDataService } from 'src/app/general/regions/list/region-list.data';
import { ContactPartView } from './contact-part.view';

interface ContactPartControllerProps extends ComponentViewProps<ContactItem[]> {
	parentModel: Model;
	mode: ControlMode;
	contextService: ContextService;
	hideModificationButton?: boolean;
	selection?: any | false;
}

@observer
export class ContactPartController extends React.Component<ContactPartControllerProps, {}> {
	@resolve
	protected postcodeService: PostcodeService;

	@observable
	dataSet: DataLocalService<ContactItem>;

	@observable
	protected cityDataService: AutocompleteListDataService;

	@observable
	protected countyDataService: AutocompleteListDataService;

	@observable
	protected regionDataService: RegionListDataService;

	@observable
	contactRegions: RegionSummary[] = [];

	currentContactRegions: number[];

	componentDidMount() {
		this.cityDataService = AutocompleteListDataService.make(this.props.contextService);
		this.countyDataService = AutocompleteListDataService.make(this.props.contextService);
		this.regionDataService = RegionListDataService.make(this.props.contextService);
		this.componentWillReceiveProps(this.props, null);
	}

	componentWillReceiveProps(nextProps: Readonly<ContactPartControllerProps>, nextContext: any) {
		this.dataSet = new DataLocalService(nextProps.model);
		if (nextProps.model.length == 0) {
			return;
		}

		const regions = [];
		for (const contact of nextProps.model) {
			if (contact.regionId != null && regions.every(e => e != contact.regionId)) {
				regions.push(contact.regionId);
			}
		}

		if (this.currentContactRegions == null || this.currentContactRegions.length != regions.length) {
			this.currentContactRegions = regions;
			this.regionDataService.addSorting('id', 'asc', true);
			this.regionDataService
				.load(function (entitySet) {
					if (regions.length > 0) {
						const predicate = regions.reduce((prev, curr) => (prev ? (`${prev} || `) : '') + `it.id == '${curr}'`, null);
						return entitySet.filter(predicate);
					}
					return entitySet;
				})
				.then((items) => {
					this.contactRegions = items;
				});
		}
	}

	@autobind
	private cityQuery(q?: string) {
		this.props.setBusy(true);
		return new Promise<QueryResult<AutocompleteSummary>>((resolve, reject) => {
			this.cityDataService.addFilter('concept', 'eq', 'contact');
			this.cityDataService.addFilter('key', 'eq', 'city');
			this.cityDataService.addSorting('value', 'asc', true);
			this.cityDataService.searchKeywords = q;
			this.cityDataService.pageSize = 100;
			return this.cityDataService
				.load(query => query)
				.then((items) => {
					this.props.setBusy(false);
					resolve({ items: items, totalCount: this.cityDataService.totalPages });
				})
				.catch(reject);
		});
	}

	@autobind
	private countyQuery(q?: string) {
		this.props.setBusy(true);
		return new Promise<QueryResult<AutocompleteSummary>>((resolve, reject) => {
			this.countyDataService.addFilter('concept', 'eq', 'contact');
			this.countyDataService.addFilter('key', 'eq', 'county');
			this.countyDataService.addSorting('value', 'asc', true);
			this.countyDataService.searchKeywords = q;
			this.countyDataService.pageSize = 100;

			return this.countyDataService
				.load(query => query)
				.then((items) => {
					this.props.setBusy(false);
					resolve({ items: items, totalCount: this.countyDataService.totalPages });
				}).catch(reject);
		});
	}

	@autobind
	private dataSetCreator<T>(items: T[]): DataSet<T> {
		return new DataLocalService(items);
	}

	@autobind
	private regionQuery(q?: string) {
		this.props.setBusy(true);
		return new Promise<QueryResult<RegionSummary>>((resolve, reject) => {
			this.regionDataService.addSorting('id', 'asc', true);
			this.regionDataService.searchKeywords = q;
			this.regionDataService.pageSize = 100;

			return this.regionDataService
				.load(query => query)
				.then((items) => {
					this.props.setBusy(false);
					const contactRegionIds = [...this.contactRegions.map(e => e.id), ...this.regionDataService.items.map(e => e.id)];
					this.contactRegions = contactRegionIds
						.map(c => this.contactRegions.find(r => r.id === c) || this.regionDataService.items.find(r => r.id === c));
					resolve({ items: items, totalCount: this.regionDataService.totalPages });
				})
				.catch(reject);
		});
	}

	render() {
		if (this.dataSet == null || this.regionDataService == null || (this.props.model.length > 0 && this.contactRegions == null)) {
			return <div>No contact information</div>
		}

		const mode = this.props.mode;
		return (
			<ContactPartView
				hideModificationButton={this.props.hideModificationButton}
				selection={this.props.selection}
				parentModel={this.props.parentModel}
				model={this.dataSet}
				dataSetCreator={this.dataSetCreator}
				mode={this.props.mode}
				setBusy={this.props.setBusy}
				cityQuery={{ query: this.cityQuery, dataSet: this.cityDataService }}
				countyQuery={{ query: this.countyQuery, dataSet: this.countyDataService }}
				contactRegions={this.contactRegions}
				search={this.postcodeService.search}
				regionSearch={this.regionQuery}
			/>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/crm/contacts/parts/contact-part.controller.tsx